import { useWallets } from '@shared/contexts/wallets-context';
import { useConnectWalletButtonStates } from '@shared/hooks/use-connect-wallet-button-states';
import clsx from 'clsx';
import { general } from 'constants/assets';
import { url } from 'constants/urls';
import { useTranslation } from 'i18n/useTranslation';
import { UserDetails } from 'interface/userDetails';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { navBarSelector, setIsLoggedIn } from 'redux/slices/navbarSlice';
import { RouterPathes } from '../../constants/router-paths';
import { handleUserDetailsRequest } from '../../context/handleUserDetailsRequest';
import { LanguageSwitcher } from '../languageSwitcher/LanguageSwitcher';
import { AccountModalButton } from '../walletConnect/accountModalButton';
import { RenderAuthButton } from './renderAuthButton';
import { RenderBurgerButton } from './renderBurgerButton';
import { RenderPowerButton } from './renderPowerButton';
import { WalletChainButton } from '../walletChainButton/walletChainButton';

export const Navbar: FC = () => {
  const dispatch = useDispatch();
  const { isLoggedIn } = useSelector(navBarSelector);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [data, setData] = useState<UserDetails | null>(null);
  const { navbarTranslations } = useTranslation();

  const returnLoginUrl = window.location.href;
  const redirectLoginUrl = url.redirectLogin(returnLoginUrl);
  const { buttonText, buttonAction, buttonClass } = useConnectWalletButtonStates();
  const { openAccountModal, isConnected, connectedAddress } = useWallets();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!isMobileMenuOpen);
  };

  const navbarItems = [
    {
      label: navbarTranslations.powerChests,
      href: import.meta.env.VITE_POWER_CHESTS_URL,
    },
    {
      label: navbarTranslations.guildBounties,
      href: import.meta.env.VITE_GUILD_BOUNTIES_URL,
    },
    {
      label: navbarTranslations.guilds,
      href: import.meta.env.VITE_GUILDS_URL,
    },
    {
      label: navbarTranslations.staking,
      href: import.meta.env.VITE_STAKING_URL,
    },
  ];

  useEffect(() => {
    (async () => {
      const { data: userData, status } = await handleUserDetailsRequest();
      setData(userData);
      dispatch(setIsLoggedIn(status === 200));
    })();
  }, [dispatch]);

  const renderWalletButton = () => (
    <>
      {isLoggedIn && !!data && !isConnected && (
        <button
          type="button"
          className={clsx(buttonClass, 'yellow-button font-border poppins-2xl my-3 h-[50px] flex-1 text-[17.5px] uppercase transition-all duration-200 hover:brightness-125 sm:h-[42px] sm:text-[17.5px] lg:my-0 lg:px-2 lg:text-[12.5px] xl:px-4 xl:text-[17.5px]')}
          onClick={buttonAction}
        >
          {buttonText}
        </button>
      )}
      {isLoggedIn && !!data && isConnected && connectedAddress && (
        <AccountModalButton openAccountModal={openAccountModal} address={connectedAddress} className="my-3 lg:my-0" />
      )}
    </>
  );

  const renderMobileMenuList = () => (
    <ul
      className={clsx(
        'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-400 absolute left-0 top-[72px] z-50 w-full overflow-y-auto rounded-b-sm bg-shell px-4 uppercase transition-all duration-300 ease-in-out',
        {
          'max-h-[calc(100vh-72px)] opacity-100': isMobileMenuOpen,
          'max-h-0 opacity-0': !isMobileMenuOpen,
        },
      )}
    >
      <li className="flex w-full font-bold">{RenderPowerButton()}</li>
      {navbarItems.map((item) => (
        <li key={item.label} className="flex w-full">
          <Link
            to={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex h-[64px] w-full items-center border-b border-separator font-poppins text-[17.5px] font-black"
          >
            {item.label}
          </Link>
        </li>
      ))}
      {RenderAuthButton({ isLoggedIn, redirectLoginUrl, toggleMobileMenu })}
      <div className={clsx(
        isLoggedIn && isConnected && connectedAddress && 'border-b border-separator',
      )}>
        {renderWalletButton()}
      </div>
      {isLoggedIn && isConnected && connectedAddress && (
        <WalletChainButton type="navbar" />
      )}
    </ul>
  );

  const renderMenuListDesktop = () => (
    <ul className="flex flex-row items-center gap-4">
      <LanguageSwitcher />
      <li className="flex font-bold">{RenderPowerButton()}</li>
      {isLoggedIn && isConnected && connectedAddress && (
        <WalletChainButton type="navbar" />
      )}
      {renderWalletButton()}
      {RenderAuthButton({ isLoggedIn, redirectLoginUrl, toggleMobileMenu })}
    </ul>
  );

  return (
    <header
      className="z-50 flex items-center justify-between bg-shell px-6 py-4 md:px-4 lg:px-10"
    >
      <div className="flex flex-row items-center gap-2 mid:mr-4 mid:gap-4">
        <Link to={RouterPathes.home}>
          <div className="flex h-[40px] items-center gap-4 md:h-[58px]">
            <img
              src={general.fabLogo}
              alt="fab-logo"
              className="w-[63.45px] md:h-[43.49px]  md:w-[75.45px]"
            />
            <div className="font-poppins font-black uppercase hover:text-neon_yellow md:block lg:hidden lg:text-[12.5px] xl:block xl:text-[17.5px]">
              {navbarTranslations.official}
              <br />
              {navbarTranslations.store}
            </div>
            <div className="h-[36px] w-[2px] bg-separator md:h-[43px]" />
          </div>
        </Link>
        {navbarItems.map((item) => (
          <Link
            key={item.label}
            to={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden font-poppins font-black uppercase hover:text-neon_yellow lg:block lg:text-[12.5px] xl:text-[17.5px]"
          >
            {item.label}
          </Link>
        ))}
      </div>
      <div className="flex items-center">
        <div className="flex flex-row items-center gap-4 lg:hidden">
          <LanguageSwitcher />
          <RenderBurgerButton
            toggleMobileMenu={toggleMobileMenu}
            isMobileMenuOpen={isMobileMenuOpen}
          />
          {renderMobileMenuList()}
        </div>
        <div className="hidden lg:block">{renderMenuListDesktop()}</div>
      </div>
    </header>
  );
};
